import {useEffect, useMemo} from "react";
import type {FormInstance} from "antd";
import {
    calculateNightCount,
    filterPublicRegistrationOptionsForParticipationFee,
    findRoomAvailability,
    getDefaultAccommodationArrival,
    getDefaultAccommodationDeparture,
    getPaymentMethodsForCategory
} from "./publicRegistrationModel";
import {buildPublicRegistrationActivityAnswers} from "./registrationPayload";
import {PayerTypeValue, PaymentMethodCategoryValue} from "./types";
import type {
    PublicEventFormResponse,
    PublicRegistrationFormValues,
    PublicRegistrationMode
} from "./types";

// Derived state of the registration form, shared by the public page and the organizer registration modal
// so both apply the same fee, activity, payment and accommodation rules.
export const useRegistrationFormState = (
    eventForm: PublicEventFormResponse | null | undefined,
    form: FormInstance<PublicRegistrationFormValues>,
    watchedValues: PublicRegistrationFormValues | undefined,
    mode: PublicRegistrationMode = "single"
) => {
    const participationMethods = useMemo(
        () => getPaymentMethodsForCategory(eventForm?.paymentMethods, PaymentMethodCategoryValue.Participation),
        [eventForm?.paymentMethods]);
    const accommodationMethods = useMemo(
        () => getPaymentMethodsForCategory(eventForm?.paymentMethods, PaymentMethodCategoryValue.Accommodation),
        [eventForm?.paymentMethods]);
    const selectedRoom = useMemo(
        () => findRoomAvailability(eventForm?.roomAvailability, watchedValues?.accommodationSelectionKey),
        [eventForm?.roomAvailability, watchedValues?.accommodationSelectionKey]);
    const reserveAccommodation = !!watchedValues?.reserveAccommodation;
    const requiresParticipationFee = eventForm?.hasParticipationFee !== false;
    const selectedParticipationFee = eventForm?.participationFees
        ?.find(fee => fee.feePriceId === Number(watchedValues?.selectedParticipationFeePriceId));
    const availableRegistrationOptions = useMemo(
        () => filterPublicRegistrationOptionsForParticipationFee(
            eventForm?.registrationOptions,
            selectedParticipationFee,
            watchedValues?.selectedOptionIds),
        [eventForm?.registrationOptions, selectedParticipationFee, watchedValues?.selectedOptionIds]);
    const answeredActivityOptionIds = new Set(
        eventForm
            ? mode === "bulk"
                ? (watchedValues?.bulkRows ?? []).flatMap(row =>
                    buildPublicRegistrationActivityAnswers({
                        ...row,
                        selectedParticipationFeePriceId: watchedValues?.selectedParticipationFeePriceId
                    }, eventForm).map(answer => answer.eventOptionId))
                : buildPublicRegistrationActivityAnswers(watchedValues ?? {}, eventForm).map(answer => answer.eventOptionId)
            : []);
    const selectedPaidOptions = (mode === "bulk"
        ? eventForm?.registrationOptions ?? []
        : availableRegistrationOptions)
        .filter(option => answeredActivityOptionIds.has(option.id))
        .filter(option => Number(option.price ?? 0) > 0);
    const selectedParticipationFeeHasPayment = !!selectedParticipationFee
        && requiresParticipationFee
        && Number(selectedParticipationFee.amount ?? 0) > 0;
    const accommodationNightCount = reserveAccommodation
        ? calculateNightCount(watchedValues?.accommodationArrivalDate, watchedValues?.accommodationDepartureDate)
        : 0;
    const accommodationRoomCount = Number(watchedValues?.accommodationRoomCount ?? 1);

    // Picking a room type proposes its default stay.
    useEffect(() => {
        if (!eventForm || !watchedValues?.reserveAccommodation) {
            return;
        }

        const room = findRoomAvailability(eventForm.roomAvailability, watchedValues.accommodationSelectionKey);
        if (!room) {
            return;
        }

        form.setFieldsValue({
            accommodationArrivalDate: getDefaultAccommodationArrival(room),
            accommodationDepartureDate: getDefaultAccommodationDeparture(room)
        });
    }, [eventForm, form, watchedValues?.accommodationSelectionKey, watchedValues?.reserveAccommodation]);

    return {
        participationMethods,
        accommodationMethods,
        selectedRoom,
        reserveAccommodation,
        requiresParticipationFee,
        selectedParticipationFee,
        availableRegistrationOptions,
        selectedParticipationFeeRequiresProof: selectedParticipationFee?.requiresParticipantTypeProof === true,
        hasParticipationOfferItems: selectedParticipationFeeHasPayment || selectedPaidOptions.length > 0,
        payerType: watchedValues?.payerType ?? PayerTypeValue.Individual,
        accommodationEstimate: selectedRoom && accommodationNightCount > 0
            ? accommodationNightCount * accommodationRoomCount * Number(selectedRoom.pricePerNight ?? 0)
            : 0
    };
};
